import { App, Plugin } from 'vue';
import posthog, { PostHogConfig } from 'posthog-js';
import { config } from '../config';

/**
 * Detects if user is likely located in the US based on timezone and language
 * @returns {boolean} True if likely US-based, false otherwise
 */
const detectIsLikelyUS = (): boolean => {
  try {
    const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
    const language = navigator.language || navigator.languages?.[0];
    const isLikelyUS =
      timeZone?.startsWith('America/') && language?.includes('en-US');
    return isLikelyUS;
  } catch {
    // Safe fallback: if detection fails, assume non-US (cookieless)
    return false;
  }
};

/**
 * PostHog configuration options
 * @constant
 */
const getPOSTHOG_CONFIG = (): Partial<PostHogConfig> => {
  // Detect user region and set persistence accordingly
  const isLikelyUS = detectIsLikelyUS();
  const persistence = isLikelyUS ? 'localStorage' : 'memory';

  return {
    api_host: config.posthogHost,
    capture_pageview: true,
    capture_pageleave: true,
    autocapture: true,
    debug: false, // Disable debug mode in all environments
    persistence,
    loaded: (posthog) => {
      // Executed once PostHog is loaded
      // Generate a unique ID for anonymous users
      const anonymousId = `anon_${Math.random().toString(36).slice(2, 15)}`;

      // Force identify an anonymous user to ensure events are tracked
      posthog.identify(anonymousId);
    },
    bootstrap: {
      distinctID: undefined, // Will be set on user login
    },
  };
};

/**
 * Vue plugin for PostHog analytics integration
 * Initializes PostHog and makes it available throughout the application
 */
export const PostHogPlugin: Plugin = {
  install(app: App): void {
    // Make PostHog available globally via the $posthog property
    app.config.globalProperties.$posthog = posthog;
    app.provide('posthog', posthog);

    // Defer PostHog initialization to avoid blocking the initial render
    if (config.posthogApiKey) {
      const initPostHog = () => {
        const posthogConfig = getPOSTHOG_CONFIG();
        posthog.init(config.posthogApiKey, posthogConfig);
      };

      if (typeof requestIdleCallback === 'undefined') {
        setTimeout(initPostHog, 0);
      } else {
        requestIdleCallback(initPostHog);
      }
    }
  },
};

export default PostHogPlugin;
